'use client';

import type { ProductDetailTags } from 'src/types/product-rankings/product-rankings-ops';

import { Chip, Stack, Typography } from '@mui/material';

import { RADIUS } from 'src/theme/ui-tokens';

import { TAG_GROUPS } from '../constants/market-rankings-sections';


/** 태그 하나를 누르면 그 태그로 검색을 좁힌다 — `param`이 검색에 넘어가는 이름 */
export type TagSelectHandler = (param: string, value: string) => void;

/**
 * 상세페이지 AI 태그 줄.
 *
 * ⚠️ 응답의 태그 필드는 **문자열이거나 배열이거나 둘 다 온다**(`productType`은 하나,
 * `concerns`는 여럿). 배열이 아니면 한 칸짜리 배열로 접어 같은 모양으로 그린다.
 *
 * ⚠️ 값이 하나도 없는 줄은 **줄 자체를 안 그린다.** 라벨만 남으면 수집이 덜 된 것처럼 보인다.
 */
export default function TagBlock({
  tags,
  onSelectTag,
  activeTags,
}: {
  tags: ProductDetailTags | null | undefined;
  onSelectTag?: TagSelectHandler;
  /** 지금 검색에 걸려 있는 태그 — 채워진 칩으로 표시한다 */
  activeTags?: Record<string, string>;
}) {
  const rows = TAG_GROUPS.map((group) => {
    const raw = tags?.[group.key];
    const values = Array.isArray(raw) ? raw : [raw].filter((value): value is string => Boolean(value));
    return values.length ? { ...group, values } : null;
  }).filter((row): row is NonNullable<typeof row> => row != null);

  if (!rows.length) return null;

  return (
    <Stack spacing={1}>
      {rows.map((row) => (
        <Stack key={row.key} direction="row" spacing={1} alignItems="flex-start">
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: 'text.disabled', width: 56, flexShrink: 0, pt: 0.4 }}
          >
            {row.label}
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
            {row.values.map((value) => {
              const active = activeTags?.[row.param] === value;
              return (
                <Chip
                  key={value}
                  size="small"
                  label={value}
                  color={row.color}
                  variant={active ? 'filled' : 'outlined'}
                  onClick={onSelectTag ? () => onSelectTag(row.param, value) : undefined}
                  sx={{ height: 24, typography: 'uiAction', borderRadius: RADIUS.pill }}
                />
              );
            })}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}
